import { authenticatedApiRequest, cleanEmptyFields } from "./core";
import type { PaginatedResponse, ContractQueryParams } from "./core";
import type { Contract, ContractDetails, CreateContractWithFile } from "@/types";

export interface PaginatedContractsResponse extends PaginatedResponse {
  results: Contract[];
}

function appendContractFields(formData: FormData, data: object) {
  for (const [key, value] of Object.entries(cleanEmptyFields(data))) {
    if (value == null) continue;
    // Objects and arrays (payees, label_share_overrides) would stringify
    // to "[object Object]" — serialize them as JSON instead.
    formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  }
}

export const contractsApi = {
  getContract: async (id: string): Promise<ContractDetails> => {
    return authenticatedApiRequest<ContractDetails>(`/contracts/${id}`);
  },
  getContracts: async (filters?: ContractQueryParams): Promise<PaginatedContractsResponse> => {
    const queryFilters = filters
      ? new URLSearchParams(
          Object.entries(filters).filter(([, v]) => v != null) as [string, string][],
        ).toString()
      : "";
    return authenticatedApiRequest<PaginatedContractsResponse>(
      `/contracts/${queryFilters ? `?${queryFilters}` : ""}`,
    );
  },
  createContract: async (contract: CreateContractWithFile): Promise<Contract> => {
    const { file, ...contractData } = contract;

    if (file) {
      // Use FormData for file upload
      const formData = new FormData();

      appendContractFields(formData, contractData);
      formData.append("file", file);

      return authenticatedApiRequest<Contract>("/contracts/", {
        method: "POST",
        body: formData,
        headers: {}, // Let the browser set Content-Type for FormData
      });
    } else {
      // Use JSON for regular requests
      return authenticatedApiRequest<Contract>("/contracts/", {
        method: "POST",
        body: JSON.stringify(cleanEmptyFields(contractData)),
      });
    }
  },
  updateContract: async (id: string, contract: CreateContractWithFile): Promise<Contract> => {
    const { file, ...contractData } = contract;

    if (file !== undefined) {
      // Use FormData when file is present (new upload) or null (removal)
      const formData = new FormData();

      appendContractFields(formData, contractData);

      // Add the file or explicitly mark for removal
      if (file) {
        formData.append("file", file);
      } else {
        // Send empty string to signal file removal
        formData.append("file", "");
      }

      return authenticatedApiRequest<Contract>(`/contracts/${id}`, {
        method: "PUT",
        body: formData,
        headers: {}, // Let the browser set Content-Type for FormData
      });
    } else {
      // Use JSON for regular requests (no file field changes)
      return authenticatedApiRequest<Contract>(`/contracts/${id}`, {
        method: "PUT",
        body: JSON.stringify(cleanEmptyFields(contractData)),
      });
    }
  },
  patchContract: async (
    id: string,
    updates: Partial<CreateContractWithFile>,
  ): Promise<Contract> => {
    const { file, ...contractData } = updates;

    if (file) {
      // Use FormData for file upload or when updating with a file
      const formData = new FormData();

      appendContractFields(formData, contractData);
      formData.append("file", file);

      return authenticatedApiRequest<Contract>(`/contracts/${id}`, {
        method: "PATCH",
        body: formData,
        headers: {}, // Let the browser set Content-Type for FormData
      });
    } else {
      // Use JSON for partial updates without files
      return authenticatedApiRequest<Contract>(`/contracts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(contractData),
      });
    }
  },
  patchContractFile: async (id: string, file: File | null): Promise<Contract> => {
    // PATCH request with only the file field using FormData
    const formData = new FormData();

    if (file) {
      formData.append("file", file);
    } else {
      // Send empty string to signal file removal
      formData.append("file", "");
    }

    return authenticatedApiRequest<Contract>(`/contracts/${id}`, {
      method: "PATCH",
      body: formData,
      headers: {}, // Let the browser set Content-Type for FormData
    });
  },
  deleteContract: async (id: string): Promise<void> => {
    await authenticatedApiRequest<void>(`/contracts/${id}`, {
      method: "DELETE",
    });
  },
};
